<template>
  <div class="tw:relative tw:w-full">
    <div v-if="initialDataLoading" class="tw:rounded-xl tw:animate-pulse tw:bg-gray-300 tw:h-[658px]"></div>
    <div v-else-if="requiredDataHasErrors">
      <p class="tw:text-center tw:text-xl">{{ $t('onboarding.appLaunchError.header') }}</p>
    </div>

    <div v-else>
      <MarketSelection v-if="currentCampaignType === 'cpc'" />
      <AutomatedMarkets
        v-if="isUsingAutomatedMarkets === true"
        class="tw:mb-6"
        :currentCampaignType="currentCampaignType"
        :marketsWithCampaign="partnerMarkets"
        :currentPartner="currentRCPartner?.partnerName"
      />

      <ManualMarkets
        v-if="isUsingAutomatedMarkets === false"
        class="tw:mt-6"
        :markets="partnerMarkets"
        :marketsWithCampaign="marketsWithCampaign"
      />
    </div>
    <FullScreenLoader v-show="requiredDataIsBeingRefetched" />
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef } from 'vue'

import MarketSelection from '@/components/rateConnect/markets/MarketSelection.vue'
import AutomatedMarkets from '@/components/rateConnect/markets/AutomatedMarkets.vue'
import ManualMarkets from '@/components/rateConnect/markets/ManualMarkets.vue'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'

import { useCampaign, usePartners, usePartnerMarkets } from '@/components/rateConnect/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

const { language } = useSessionValues()

const {
  isFetching: isCampaignsFetching,
  isLoading: isCampaignsLoading,
  isError: isCampaignsError,
  isUsingAutomatedMarkets,
  currentPartner,
  currentCampaignType,
} = useCampaign(selectedAccommodationId)

const {
  isFetching: isPartnersFetching,
  isLoading: isPartnersLoading,
  isError: isPartnersError,
  partnersData,
} = usePartners(selectedAccommodationId)

const {
  isFetching: isMarketsFetching,
  isInitialLoading: isMarketsLoading,
  isError: isMarketsError,
  partnerMarkets,
} = usePartnerMarkets(selectedAccommodationId, language, currentPartner)

const marketsWithCampaign = computed(() => partnerMarkets.value.filter(item => item.hasCampaign))

const currentRCPartner = computed(() => partnersData.value?.find(item => item.partnerId === currentPartner.value))

const initialDataLoading: ComputedRef<boolean> = computed(
  () => isCampaignsLoading.value || isPartnersLoading.value || isMarketsLoading.value
)

const requiredDataHasErrors: ComputedRef<boolean> = computed(
  () => isCampaignsError.value || isPartnersError.value || isMarketsError.value
)

const requiredDataIsBeingRefetched: ComputedRef<boolean> = computed(
  () => !initialDataLoading.value && (isCampaignsFetching.value || isPartnersFetching.value || isMarketsFetching.value)
)
</script>
